/**
 * usePanelSearch - Panel Search Logic Hook
 * 
 * Extracts search functionality from PagePanel.tsx:
 * - Search query state management
 * - Debounced search execution
 * - Search results handling
 * - Active search mode tracking
 */

import { useState, useEffect, useCallback } from 'react';
import { searchApi } from '../services/api';
import { logger } from '../utils/logger';
import type { ReferenceDocumentCard, PagePanelId } from '../types/panel-types';

interface UsePanelSearchProps {
  panelType: PagePanelId;
  initialSearchQuery?: string;
  debounceMs?: number;
}

interface UsePanelSearchReturn {
  // State
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: ReferenceDocumentCard[];
  isSearching: boolean;
  hasActiveSearch: boolean;
  error: string | null;
  
  // Actions
  clearSearch: () => void;
  executeSearch: (query: string) => Promise<void>;
}

export function usePanelSearch({
  panelType,
  initialSearchQuery = '',
  debounceMs = 300,
}: UsePanelSearchProps): UsePanelSearchReturn {
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<ReferenceDocumentCard[]>([]);
  const [hasActiveSearch, setHasActiveSearch] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Execute search against the API
   */
  const executeSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      setHasActiveSearch(false);
      return;
    }

    setIsSearching(true);
    setError(null);
    
    try {
      const response = await searchApi.searchAllDocuments(query, 20);
      const results = response.results || [];
      
      interface SearchResultItem {
        id?: string;
        documentId?: string;
        type?: string;
        documentNumber?: string;
        standardNumber?: string;
        title?: string;
        description?: string;
        snippet?: string;
        aiSummary?: string;
        summaryGeneratedAt?: string;
      }
      
      // Filter results based on panel type
      const filtered = panelType === 'reference-library'
        ? results.filter((r: SearchResultItem) => r.type === 'standard' || r.type === 'company')
        : results;
      
      const cards: ReferenceDocumentCard[] = filtered.map((r: SearchResultItem) => {
        const standardNum = r.documentNumber || r.standardNumber;
        const rawTitle = r.title || '';
        // Use standardNumber as title if raw title is a ref_ UUID
        const displayTitle = rawTitle.startsWith('ref_') && standardNum 
          ? standardNum 
          : rawTitle;
        
        return {
          documentId: r.id || r.documentId,
          title: displayTitle,
          documentType: r.type === 'standard' ? 'industry-standard' : 'company-reference',
          standardNumber: standardNum,
          description: r.snippet,
          aiSummary: r.aiSummary,
          indexStatus: 'completed',
          createdAt: r.createdAt || new Date().toISOString(),
          relevanceScore: r.score,
          matchedPassage: r.snippet,
          relatedDocuments: r.relatedDocuments,
        };
      });
      
      setSearchResults(cards);
      setHasActiveSearch(true);
    } catch (err: unknown) {
      logger.error('Search failed:', err);
      setError(err.message || 'Search failed');
      setHasActiveSearch(false);
    } finally {
      setIsSearching(false);
    }
  }, [panelType]);

  /**
   * Clear search state
   */
  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
    setHasActiveSearch(false);
    setError(null);
  }, []);

  // Debounced search effect
  useEffect(() => {
    if (!searchQuery.trim()) {
      // Clear search state when query is empty
      setSearchResults([]);
      setIsSearching(false);
      setHasActiveSearch(false);
      return;
    }
    
    const timeoutId = setTimeout(() => {
      executeSearch(searchQuery);
    }, debounceMs);
    
    return () => clearTimeout(timeoutId);
  }, [searchQuery, debounceMs, executeSearch]);

  return {
    searchQuery,
    setSearchQuery,
    searchResults,
    isSearching,
    hasActiveSearch,
    error,
    clearSearch,
    executeSearch,
  };
}
